import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ContentService } from '../../services/content.service';
import {
  E_MANAGE_FIELDS,
  IMySettings,
  IMySettings_Folder,
  IMySettings_Topics,
  IMySettingsRequest,
} from './manage-my-settings.model';
import {
  E_TITLE_SIZES,
  ThreeColumnTemplateComponent,
} from '@templates/three-column-template/three-column-template.component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { _CLEAR, HelperService } from '../../services/helper.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { BUTTON_SIZE, BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-my-topics',
  imports: [
    ThreeColumnTemplateComponent,
    MatCheckbox,
    ButtonComponent,
    RouterLink,
    TranslateModule,
  ],
  templateUrl: './manage-my-topics.component.html',
  styleUrl: './manage-my-topics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyTopicsComponent implements OnInit {

  CONTENT = inject(ContentService);
  HELPER = inject(HelperService);

  showLoader = signal(false);

  settings = signal<IMySettings | null>(null);
  folderSettings = computed(() => this.settings()?.folderSettings || []);
  topicSettings = computed(() => this.settings()?.topicSettings || []);

  websiteChecked = computed(() => this.settings()?.topicSettings.every(item => item.website));
  websiteIndeterminate = computed(() => {
    return !this.websiteChecked() && this.settings()?.topicSettings.some(item => item.website);
  });

  emailChecked = computed(() => this.settings()?.topicSettings.every(item => item.email));
  emailIndeterminate = computed(() => {
    return !this.emailChecked() && this.settings()?.topicSettings.some(item => item.email);
  });

  emailFolderChecked = computed(() => this.settings()?.folderSettings.every(item => item.email));
  emailFolderIndeterminate = computed(() => {
    return !this.emailChecked() && this.settings()?.folderSettings.some(item => item.email);
  });

  postBody = computed<IMySettingsRequest>(() => {
    return {
      emailTopicIds: this.settings()?.topicSettings
        .filter(item => item.email)
        .map(item => item.topicId) || [],
      websiteTopicIds: this.settings()?.topicSettings
        .filter(item => item.website)
        .map(item => item.topicId) || [],
      emailFolderItemIds: this.settings()?.folderSettings
        .filter(item => item.email)
        .map(item => item.folderItemId) || [],
    };
  });


  ngOnInit() {
    this.getMySettings();
  }

  getMySettings(): void {
    this.showLoader.set(true);
    this.CONTENT.getMySettings().subscribe(data => {
      this.showLoader.set(false);
      this.settings.set(data);
    });
  }


  toggleAllTopics($event: MatCheckboxChange, field: E_MANAGE_FIELDS) {
    this.settings()?.topicSettings.forEach(item => {
      item[field] = $event.checked;
    });
    this.updateSettings();
  }

  toggleTopicItem(item: IMySettings_Topics, $event: MatCheckboxChange, field: E_MANAGE_FIELDS) {
    item[field] = $event.checked;
    this.updateSettings();
  }

  toggleAllFolder($event: MatCheckboxChange) {
    this.settings()?.folderSettings.forEach(item => {
      item.email = $event.checked;
    });
    this.updateSettings();
  }

  toggleFolderItem(item: IMySettings_Folder, $event: MatCheckboxChange) {
    item.email = $event.checked;
    this.updateSettings();
  }

  updateSettings() {
    this.settings.set(_CLEAR(this.settings()));
  }

  saveMySettings() {
    this.CONTENT.saveMySettings(this.postBody())
      .subscribe(() => {
        this.getMySettings();
      });
  }


  protected readonly E_TITLE_SIZES = E_TITLE_SIZES;
  protected readonly E_MANAGE_FIELDS = E_MANAGE_FIELDS;
  protected readonly BUTTON_SIZE = BUTTON_SIZE;
  protected readonly BUTTON_TYPES = BUTTON_TYPES;
}
